import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Collapse,
    Dialog,
    IconButton,
    LinearProgress,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import {
    expertProductTestingEndpoint,
    expertProductTestingSaveEndpoint,
} from "../../../api/endpoints";

import {
    PRODUCT_TESTING_SECTIONS,
    PRODUCT_TESTING_TOKENS,
    PRODUCT_TESTING_TOTAL,
    ProductTestingAnswers,
    ProductTestingForm,
    answeredCount,
    apiErrorCode,
} from "../../../components/ProductTesting/constants";

const T = PRODUCT_TESTING_TOKENS;
const SECTIONS = PRODUCT_TESTING_SECTIONS;
const TOTAL = PRODUCT_TESTING_TOTAL;

const footerNote = (submitted: boolean) =>
    submitted
        ? "Submitted. Answers are locked and can no longer be edited."
        : "Save keeps your progress. Submit when every question is answered.";

const SAVE_ERRORS: Record<string, string> = {
    ProductTestingIncomplete: "Answer every question before submitting.",
    ProductTestingAlreadySubmitted:
        "This form was already submitted and can no longer be edited.",
    ProductNotOnProject: "That product is no longer on this project.",
    NotConfirmedForProject: "You are not confirmed on this project yet.",
    NotOptedIn: "Opt in to this project before giving feedback.",
    NotOnThisProject: "You are not assigned to this project.",
};

type Answers = ProductTestingAnswers;

interface Props {
    projectId: number | string;
    onClose: () => void;
}

const buttonBase = {
    padding: "9px 22px",
    borderRadius: "9px",
    fontFamily: "CircularStd",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "all .15s",
    whiteSpace: "nowrap" as const,
};

export const ProductTestingModal: FC<Props> = ({ projectId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [forms, setForms] = useState<ProductTestingForm[]>([]);
    const [drafts, setDrafts] = useState<Record<number, Answers>>({});
    const [expanded, setExpanded] = useState<number | null>(null);
    const [savingProduct, setSavingProduct] = useState<number | null>(null);

    // Parents pass an inline onClose (new identity every render), and the
    // card re-renders on any store change — if onClose were a dep of `load`,
    // every notification would refetch and silently wipe unsaved drafts.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const load = useCallback(() => {
        setLoading(true);
        axiosInstant
            .get(expertProductTestingEndpoint(projectId))
            .then((res) => {
                const loaded: ProductTestingForm[] = res.data?.forms || [];
                setForms(loaded);
                setDrafts(
                    loaded.reduce(
                        (acc: Record<number, Answers>, form) => ({
                            ...acc,
                            [form.desireid]: { ...form.answers },
                        }),
                        {}
                    )
                );
                const firstOpen = loaded.find((form) => !form.submitted_at);
                setExpanded((firstOpen || loaded[0])?.desireid ?? null);
            })
            .catch((error) => {
                toast.error(
                    SAVE_ERRORS[apiErrorCode(error)] ||
                        "Could not load the feedback form."
                );
                onCloseRef.current();
            })
            .finally(() => setLoading(false));
    }, [projectId]);

    useEffect(() => load(), [load]);

    const setAnswer = (desireid: number, field: string, value: string) =>
        setDrafts((prev) => ({
            ...prev,
            [desireid]: { ...(prev[desireid] || {}), [field]: value },
        }));

    const save = async (form: ProductTestingForm, submit: boolean) => {
        const answers = drafts[form.desireid] || {};
        const remaining = TOTAL - answeredCount(answers);
        if (submit && remaining > 0) {
            toast.error(
                remaining === 1
                    ? "1 question left to answer before submitting."
                    : `${remaining} questions left to answer before submitting.`
            );
            return;
        }
        setSavingProduct(form.desireid);
        try {
            const { data } = await axiosInstant.post(
                expertProductTestingSaveEndpoint(projectId, form.desireid),
                { ...answers, submit }
            );
            const saved: ProductTestingForm = data;
            setForms((prev) =>
                prev.map((entry) =>
                    entry.desireid === saved.desireid ? saved : entry
                )
            );
            setDrafts((prev) => ({
                ...prev,
                [saved.desireid]: { ...saved.answers },
            }));
            if (submit) {
                toast.success("Successfully submitted the feedback form.");
            } else {
                toast.success("Successfully saved the feedback form as a draft.");
            }
            if (submit) {
                const nextOpen = forms.find(
                    (entry) =>
                        entry.desireid !== saved.desireid && !entry.submitted_at
                );
                setExpanded(nextOpen ? nextOpen.desireid : null);
            }
        } catch (error: any) {
            toast.error(
                SAVE_ERRORS[apiErrorCode(error)] ||
                    "Could not save the feedback form. Please try again."
            );
        } finally {
            setSavingProduct(null);
        }
    };

    const multiProduct = forms.length > 1;

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            PaperProps={{
                sx: {
                    width: 560,
                    maxWidth: "100%",
                    borderRadius: "18px",
                    boxShadow: "0 24px 70px rgba(0,0,0,.2)",
                    fontFamily: "CircularStd",
                    overflow: "hidden",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "18px 22px 16px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "10px",
                            backgroundColor: T.goldTile,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <ScienceOutlinedIcon sx={{ fontSize: 19, color: T.goldInk }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: "CircularStd",
                                fontSize: 18,
                                fontWeight: 800,
                                color: T.ink,
                                letterSpacing: "-0.3px",
                            }}
                        >
                            Product Feedback Form
                        </Typography>
                        <Typography
                            sx={{ fontFamily: "CircularStd", fontSize: 12, color: T.muted, mt: "1px" }}
                        >
                            Share your hands-on experience
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        width: 32,
                        height: 32,
                        border: `1px solid ${T.border}`,
                        color: T.muted,
                        flexShrink: 0,
                        "&:hover": { borderColor: T.ink, color: T.ink },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            <Box sx={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
                {loading && (
                    <Box sx={{ px: "22px", py: 5 }}>
                        <LinearProgress
                            sx={{
                                height: 3,
                                backgroundColor: T.rule,
                                "& .MuiLinearProgress-bar": { backgroundColor: T.goldSoft },
                            }}
                        />
                    </Box>
                )}

                {!loading && forms.length === 0 && (
                    <Typography
                        sx={{
                            fontFamily: "CircularStd",
                            textAlign: "center",
                            py: 6,
                            px: 3,
                            color: T.muted,
                            fontSize: 13.5,
                        }}
                    >
                        This project has no products to give feedback on yet.
                    </Typography>
                )}

                {!loading &&
                    forms.map((form) => {
                        const answers = drafts[form.desireid] || {};
                        const answered = answeredCount(answers);
                        const complete = answered === TOTAL;
                        const submitted = !!form.submitted_at;
                        const busy = savingProduct === form.desireid;
                        const isOpen = expanded === form.desireid;
                        const readyToSubmit = complete && !submitted;
                        return (
                            <Box key={form.desireid}>
                                <Box
                                    onClick={() =>
                                        setExpanded(isOpen && multiProduct ? null : form.desireid)
                                    }
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1.5,
                                        p: "14px 22px",
                                        backgroundColor: T.barBg,
                                        borderTop: `1px solid #F0EDE8`,
                                        borderBottom: `1px solid #F0EDE8`,
                                        cursor: multiProduct ? "pointer" : "default",
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: "CircularStd",
                                                fontSize: 14.5,
                                                fontWeight: 700,
                                                color: T.ink,
                                            }}
                                        >
                                            {form.product?.title || `Product ${form.desireid}`}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                mt: 0.75,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.75,
                                                    fontFamily: "CircularStd",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    padding: "3px 10px",
                                                    borderRadius: "20px",
                                                    color: submitted ? T.doneInk : T.pendingInk,
                                                    backgroundColor: submitted ? T.doneBg : T.pendingBg,
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: "50%",
                                                        backgroundColor: submitted ? T.doneDot : T.pendingDot,
                                                    }}
                                                />
                                                {submitted ? "Submitted" : "Not submitted"}
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: "CircularStd",
                                                    fontSize: 11.5,
                                                    color: T.muted,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {answered}/{TOTAL} answered
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {multiProduct && (
                                        <ExpandMoreIcon
                                            sx={{
                                                fontSize: 20,
                                                color: T.muted,
                                                flexShrink: 0,
                                                transform: isOpen ? "none" : "rotate(-90deg)",
                                                transition: "transform .2s",
                                            }}
                                        />
                                    )}
                                </Box>

                                <Box sx={{ height: 3, backgroundColor: T.rule }}>
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: `${Math.round((answered / TOTAL) * 100)}%`,
                                            background: `linear-gradient(90deg,${T.goldSoft},#DFC9A3)`,
                                            transition: "width .4s ease",
                                        }}
                                    />
                                </Box>

                                <Collapse in={isOpen} unmountOnExit>
                                    <Box sx={{ p: "20px 22px 8px" }}>
                                        {SECTIONS.map((section) => (
                                            <Box key={section.num} sx={{ mb: "26px" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1.25,
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: 11,
                                                            fontWeight: 800,
                                                            color: T.goldSoft,
                                                            letterSpacing: "0.5px",
                                                        }}
                                                    >
                                                        {section.num}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color: T.sub,
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.6px",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {section.title}
                                                    </Typography>
                                                    <Box sx={{ flex: 1, height: "1px", backgroundColor: T.rule }} />
                                                </Box>

                                                {section.questions.map((question, questionIndex) => {
                                                    const value = answers[question.field] || "";
                                                    const filled = value.trim().length > 0;
                                                    return (
                                                        <Box key={question.field} sx={{ mb: "18px", pl: "2px" }}>
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "flex-start",
                                                                    gap: "9px",
                                                                    mb: "9px",
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        borderRadius: "50%",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        flexShrink: 0,
                                                                        mt: "1px",
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: 11,
                                                                        fontWeight: 700,
                                                                        transition: "all .2s",
                                                                        backgroundColor: filled ? T.doneDot : T.badgeBg,
                                                                        color: filled ? "#FFF" : T.muted,
                                                                    }}
                                                                >
                                                                    {filled ? (
                                                                        <CheckIcon sx={{ fontSize: 12 }} />
                                                                    ) : (
                                                                        questionIndex + 1
                                                                    )}
                                                                </Box>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: 14,
                                                                        fontWeight: 600,
                                                                        color: T.ink,
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                >
                                                                    {question.label}
                                                                </Typography>
                                                            </Box>
                                                            {question.hint && (
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: 12,
                                                                        color: T.muted,
                                                                        lineHeight: 1.5,
                                                                        m: "-2px 0 9px 29px",
                                                                    }}
                                                                >
                                                                    {question.hint}
                                                                </Typography>
                                                            )}
                                                            <Box
                                                                component="textarea"
                                                                rows={question.hint ? 3 : 2}
                                                                placeholder="Your answer…"
                                                                value={value}
                                                                readOnly={submitted}
                                                                onChange={(
                                                                    event: React.ChangeEvent<HTMLTextAreaElement>
                                                                ) =>
                                                                    !submitted &&
                                                                    setAnswer(
                                                                        form.desireid,
                                                                        question.field,
                                                                        event.target.value
                                                                    )
                                                                }
                                                                sx={{
                                                                    width: "100%",
                                                                    padding: "11px 14px",
                                                                    border: `1.5px solid ${
                                                                        filled ? T.filledBorder : T.border
                                                                    }`,
                                                                    borderRadius: "11px",
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: "13.5px",
                                                                    color: T.ink,
                                                                    outline: "none",
                                                                    resize: "vertical",
                                                                    backgroundColor: filled ? T.filledBg : "var(--surface)",
                                                                    lineHeight: 1.5,
                                                                    transition: "border-color .15s, box-shadow .15s",
                                                                    "&:focus": {
                                                                        borderColor: T.goldSoft,
                                                                        boxShadow: `0 0 0 3px rgba(200,169,126,.14)`,
                                                                    },
                                                                    "&::placeholder": { color: T.placeholder },
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1.5,
                                            p: "14px 22px",
                                            borderTop: `1px solid ${T.rule}`,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: "CircularStd",
                                                fontSize: 12,
                                                color: T.muted,
                                                flex: 1,
                                                minWidth: 180,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {readyToSubmit && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.75,
                                                        color: T.goldInk,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />
                                                    All answered, ready to submit
                                                </Box>
                                            )}
                                            {!readyToSubmit && footerNote(submitted)}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            {!submitted && (
                                            <Box
                                                component="button"
                                                type="button"
                                                disabled={busy}
                                                onClick={() => save(form, false)}
                                                sx={{
                                                    ...buttonBase,
                                                    backgroundColor: "var(--surface)",
                                                    borderColor: T.border,
                                                    color: T.sub,
                                                    "&:hover": { borderColor: T.ink, color: T.ink },
                                                    "&:disabled": { opacity: 0.6, cursor: "default" },
                                                }}
                                            >
                                                Save
                                            </Box>
                                            )}
                                            {!submitted && (
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    disabled={busy || !complete}
                                                    onClick={() => save(form, true)}
                                                    sx={{
                                                        ...buttonBase,
                                                        // badgeBg (a real fill), not border: --border is a
                                                        // translucent hairline in dark and would leave the
                                                        // disabled button with no visible body.
                                                        backgroundColor: complete ? T.gold : T.badgeBg,
                                                        color: complete ? "#FFF" : T.placeholder,
                                                        cursor: complete ? "pointer" : "not-allowed",
                                                        "&:hover": complete
                                                            ? { backgroundColor: T.goldHover }
                                                            : {},
                                                    }}
                                                >
                                                    Submit
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
            </Box>
        </Dialog>
    );
};
